/**
 * 자재 일괄 등록 다이얼로그.
 *
 * 흐름: 엑셀 파싱 결과(headers/dataRows) 를 prop 으로 받아
 *  1. 헤더 자동 매핑(이름/수량/제품업체/분류/단위/비고).
 *  2. 미스매치 헤더는 사용자가 드롭다운으로 수동 지정.
 *  3. 행 검증을 즉시 반영 — 유효/오류 카운트 + 오류 상세 표시.
 *  4. "등록 N건" 버튼 → 부모가 받은 onConfirm(payloads) 로 위임.
 *
 * 등록(POST) 진행 상태는 부모(MaterialsSection)가 표시 — 다이얼로그는 매핑/미리보기에만 집중.
 */
import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Select,
  MenuItem,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { CreateSiteMaterialRequest } from '../../services/SiteMaterialService';
import {
  autoMapHeaders,
  buildPayloads,
  findDuplicateFieldHeaders,
  MATERIAL_FIELD_LABELS,
  MaterialField,
  SheetGrid,
} from './materialsExcelImport';

const FIELD_OPTIONS: MaterialField[] = [
  'name',
  'quantity',
  'vendor',
  'category',
  'unit',
  'note',
  'ignore',
];

interface Props {
  open: boolean;
  grid: SheetGrid | null;
  sheetName: string | null;
  /** 매핑 검증 통과한 payload 목록을 부모에게 전달 — 부모가 POST 처리. */
  onConfirm: (payloads: CreateSiteMaterialRequest[]) => Promise<void> | void;
  onClose: () => void;
  /** 부모가 POST 진행 중일 때 true — 버튼 비활성화/문구 변경에 사용. */
  submitting?: boolean;
}

const MaterialsImportDialog: React.FC<Props> = ({
  open,
  grid,
  sheetName,
  onConfirm,
  onClose,
  submitting = false,
}) => {
  const theme = useTheme();
  // 초기 매핑은 자동 매핑 결과. 그리드가 바뀔 때마다 재계산.
  const initialMapping = useMemo<MaterialField[]>(
    () => (grid ? autoMapHeaders(grid.headers) : []),
    [grid],
  );

  const [mapping, setMapping] = useState<MaterialField[]>(initialMapping);

  // 그리드 prop 변경 시 매핑 동기화. (다이얼로그를 같은 인스턴스로 재사용하므로 명시적으로 처리.)
  React.useEffect(() => {
    setMapping(initialMapping);
  }, [initialMapping]);

  const handleMappingChange = (headerIndex: number, value: MaterialField) => {
    setMapping((prev) => {
      const next = [...prev];
      next[headerIndex] = value;
      return next;
    });
  };

  const { payloads, errors, mappingError } = useMemo(() => {
    if (!grid) return { payloads: [], errors: [], mappingError: null };
    return buildPayloads(grid, mapping);
  }, [grid, mapping]);

  const duplicates = useMemo(() => findDuplicateFieldHeaders(mapping), [mapping]);

  const handleConfirm = async () => {
    if (mappingError || duplicates.length > 0 || payloads.length === 0) return;
    await onConfirm(payloads);
  };

  if (!grid) return null;

  const validCount = payloads.length;
  const errorCount = errors.length;
  const totalRows = grid.dataRows.length;
  const canConfirm =
    !mappingError && duplicates.length === 0 && validCount > 0 && !submitting;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>
          엑셀 자재 일괄 등록
          {sheetName && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              · {sheetName}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" disabled={submitting} aria-label="닫기">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip
              size="small"
              color={validCount > 0 ? 'success' : 'default'}
              label={`유효 ${validCount}건`}
            />
            {errorCount > 0 && (
              <Chip size="small" color="warning" label={`오류 ${errorCount}건`} />
            )}
            <Chip size="small" variant="outlined" label={`전체 ${totalRows}건`} />
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              엑셀 첫 행을 컬럼 헤더로 인식합니다. 각 헤더에 매핑할 필드를 확인하세요.
            </Typography>
          </Stack>

          {mappingError && (
            <Alert severity="error">{mappingError}</Alert>
          )}

          {duplicates.length > 0 && (
            <Alert severity="warning">
              같은 필드가 여러 컬럼에 매핑되었습니다: {duplicates.map((d) => MATERIAL_FIELD_LABELS[d]).join(', ')}.
              하나만 남기고 나머지는 "무시"로 바꾸세요.
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 64, bgcolor: 'background.paper' }}>#</TableCell>
                  {grid.headers.map((h, i) => (
                    <TableCell key={i} sx={{ minWidth: 160, bgcolor: 'background.paper' }}>
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary" noWrap title={h}>
                          {h}
                        </Typography>
                        <Select
                          size="small"
                          value={mapping[i] ?? 'ignore'}
                          onChange={(e) => handleMappingChange(i, e.target.value as MaterialField)}
                          disabled={submitting}
                        >
                          {FIELD_OPTIONS.map((f) => (
                            <MenuItem key={f} value={f}>
                              {MATERIAL_FIELD_LABELS[f]}
                              {(f === 'name' || f === 'quantity' || f === 'vendor') && (
                                <Typography component="span" variant="caption" color="error" sx={{ ml: 0.5 }}>
                                  *
                                </Typography>
                              )}
                            </MenuItem>
                          ))}
                        </Select>
                      </Stack>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {grid.dataRows.map((row, ri) => {
                  const rowError = errors.find((e) => e.rowIndex === ri);
                  return (
                    <TableRow
                      key={ri}
                      sx={{
                        bgcolor: rowError ? alpha(theme.palette.warning.main, 0.12) : 'transparent',
                      }}
                    >
                      <TableCell>
                        {rowError ? (
                          <Tooltip title={rowError.message}>
                            <WarningIcon fontSize="small" color="warning" />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            {ri + 1}
                          </Typography>
                        )}
                      </TableCell>
                      {grid.headers.map((_, ci) => (
                        <TableCell key={ci} sx={{ fontSize: '0.85rem' }}>
                          {row[ci] ?? ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
                {grid.dataRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={grid.headers.length + 1} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        데이터 행이 없습니다.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {errorCount > 0 && (
            <Alert severity="info">
              오류 {errorCount}건은 등록에서 제외됩니다. 엑셀에서 수정 후 다시 업로드하거나, 유효 {validCount}건만 먼저 등록할 수 있습니다.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          취소
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm}
        >
          {submitting ? `등록 중…` : `유효 ${validCount}건 등록`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaterialsImportDialog;
